import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, RotateCcw, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtDate, todayISO } from "@/lib/format";

export const REASONS = { RICONSEGNATO: "Riconsegnato", RESTITUITO: "Restituito", BLOCCATO: "Bloccato" };
const PILL = { RICONSEGNATO: "border-sky-200 bg-sky-50 text-sky-700", RESTITUITO: "border-slate-200 bg-slate-100 text-slate-700", BLOCCATO: "border-rose-200 bg-rose-50 text-rose-700" };

export const ArchiveTelepassDialog = ({ item, onClose, onDone }) => {
  const [f, setF] = useState({ reason: "RICONSEGNATO", date: todayISO(), note: "" });
  useEffect(() => { if (item) setF({ reason: "RICONSEGNATO", date: todayISO(), note: "" }); }, [item]);
  if (!item) return null;
  const save = async () => {
    try { await api.post(`/telepass/${item.id}/archive`, f); toast.success(`Telepass ${item.number} spostato in archivio`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="telepass-archive-form">
        <DialogHeader>
          <DialogTitle>Dismetti Telepass {item.number}</DialogTitle>
          <DialogDescription>Esce dall'elenco e dai conteggi ma resta consultabile in Archivio.{item.plate && ` Verrà scollegato da ${item.plate}.`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Motivo</Label>
            <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
              <SelectTrigger data-testid="telepass-archive-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(REASONS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`telepass-archive-reason-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="telepass-archive-date" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="telepass-archive-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} data-testid="telepass-archive-confirm"><Archive className="mr-2 h-4 w-4" /> Sposta in archivio</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TelepassArchive = ({ open, stationId, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/telepass/archive", { params: { station_id: stationId } }).then((r) => setRows(r.data));
  useEffect(() => { if (open) load(); }, [open, stationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (t) => {
    try { await api.post(`/telepass/${t.id}/restore`); toast.success(`Telepass ${t.number} ripristinato`); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = q.trim().toLowerCase();
  const list = rows.filter((t) => !s || [t.number, t.internal_number, t.archived_plate, t.archive_note, REASONS[t.archive_reason], t.station_name].some((x) => (x || "").toLowerCase().includes(s)));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="telepass-archive-dialog">
        <DialogHeader>
          <DialogTitle>Archivio Telepass dismessi ({rows.length})</DialogTitle>
          <DialogDescription>Riconsegnati, restituiti o bloccati: non compaiono nell'elenco ma restano in memoria.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero, targa, motivo..." className="pl-9" data-testid="telepass-archive-search" />
        </div>
        <div className="divide-y rounded-lg border">
          {list.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`telepass-archive-row-${t.id}`}>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <b className="font-mono-num">{t.number}</b>{t.internal_number && <span className="text-xs text-slate-500">int. {t.internal_number}</span>}
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${PILL[t.archive_reason]}`}>{REASONS[t.archive_reason]}</span>
                </p>
                <p className="mt-1 text-xs text-slate-500">{t.station_name} · il {fmtDate(t.archive_date)}{t.archived_by_name && ` da ${t.archived_by_name}`}{t.archived_plate && ` · era su ${t.archived_plate}`}{t.area_c && " · Area C"}{t.archive_note && ` · ${t.archive_note}`}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => restore(t)} data-testid={`telepass-restore-${t.id}`}><RotateCcw className="mr-1 h-4 w-4" /> Ripristina</Button>
            </div>
          ))}
          {!list.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="telepass-archive-empty">Nessun Telepass in archivio.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
